/**
 * Common validation schemas used across multiple domains
 * These are reusable Zod schemas that enforce consistent validation patterns
 */
import { z } from "zod";
// ===== ID SCHEMAS =====
/**
 * UUID format validation
 * Used for: all entity IDs (companyId, userId, locationId, etc.)
 */
export const uuidSchema = z.uuid();
// ===== TEXT FIELD SCHEMAS =====
/**
 * Name field validation
 * Used for: company names, location names, tag names, etc.
 * Rules: trimmed, 1-255 characters
 */
export const nameSchema = z.string().trim().min(1).max(255);
/**
 * Description field validation
 * Used for: short descriptions
 * Rules: max 1000 characters, optional
 */
export const descriptionSchema = z.string().max(1000).optional();
/**
 * Notes field validation
 * Used for: longer notes/comments
 * Rules: max 10000 characters (~10KB text), optional
 */
export const notesSchema = z.string().max(10000).optional();
/**
 * Job code validation
 * Used for: external system job codes (payroll, POS integration)
 * Rules: trimmed, 1-100 characters, optional
 */
export const jobCodeSchema = z.string().trim().min(1).max(100).optional();
/**
 * TTOC Job Code validation
 * Used for: TTOC (Tax on Tips of Cash) job classification codes (for tax on tips legislation)
 * Rules: exactly 3 digits, 100-899 inclusive
 */
export const ttocJobCodeSchema = z.string().regex(/^[1-8]\d{2}$/, {
    message: "TTOC job code must be 3 digits from 100-899",
});
// ===== FORMAT SCHEMAS =====
/**
 * Email format validation
 * Used for: user emails
 */
export const emailSchema = z.email();
/**
 * Phone number validation
 * Used for: phone numbers (optional)
 * Note: Lenient validation - accepts various formats
 */
export const phoneSchema = z.string();
/**
 * Hex color format validation
 * Used for: tag colors, role colors, etc.
 * Rules: Must be #RGB or #RRGGBB format
 */
export const hexColorSchema = z
    .string()
    .regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, {
    message: "Must be a valid hex color (e.g., #FF5733 or #F57)",
});
/**
 * Optional hex color validation
 * Same as hexColorSchema but allows undefined
 */
export const hexColorOptionalSchema = hexColorSchema.optional();
// ===== DATE SCHEMAS =====
/**
 * Date coercion from string to Date object
 * Used for: startTime, endTime, dates in request bodies
 * Note: Automatically converts ISO date strings to Date objects
 */
export const dateSchema = z.coerce.date();
/**
 * ISO datetime string validation
 * Used for: dates that should remain as strings
 */
export const dateStringSchema = z.string().datetime();
export const timeHhmmSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Must be HH:mm format (e.g., 09:00)");
// ===== NUMBER SCHEMAS =====
/**
 * Positive integer validation
 * Used for: counts, IDs (numeric)
 */
export const positiveIntSchema = z.number().int().positive();
/**
 * Non-negative integer validation
 * Used for: counts that can be zero, sort orders
 */
export const nonNegativeIntSchema = z.number().int().nonnegative();
/**
 * Optional non-negative integer
 * Used for: optional sort orders, optional counts
 */
export const nonNegativeIntOptionalSchema = nonNegativeIntSchema.optional();
// ===== CUSTOM VALIDATORS =====
/**
 * Validates that at least one field is present in an update object
 * Used for: update endpoints that require at least one field to update
 */
export function atLeastOneField(schema) {
    return schema.refine((data) => Object.keys(data).length > 0, {
        message: "At least one field must be provided for update",
    });
}
/**
 * Validates that endTime is after startTime
 * Used for: shift creation, time ranges
 */
export function validateTimeRange(schema) {
    return schema.refine((data) => data.endTime > data.startTime, {
        message: "End time must be after start time",
        path: ["endTime"],
    });
}
// ===== ROUTE PARAMETER SCHEMAS =====
/**
 * Generic ID parameter validation
 * Used for: route parameters like /:id
 * Pattern: { id: "uuid" }
 */
export const IdParamSchema = z.object({
    id: uuidSchema,
});
/**
 * Company ID parameter validation
 * Used for: route parameters like /:companyId or /companies/:companyId
 * Pattern: { companyId: "uuid" }
 */
export const CompanyIdParamSchema = z.object({
    companyId: uuidSchema,
});
/**
 * Task ID parameter validation
 * Used for: nested task routes like /checklists/:id/tasks/:taskId
 * Pattern: { taskId: "uuid" }
 */
export const TaskIdParamSchema = z.object({
    taskId: uuidSchema,
});
/**
 * Shift ID parameter validation
 * Used for: route parameters like /shifts/:shiftId
 * Pattern: { shiftId: "uuid" }
 */
export const ShiftIdParamSchema = z.object({
    shiftId: uuidSchema,
});
/**
 * Employee ID parameter validation
 * Used for: route parameters like /employees/:employeeId/shifts
 * Pattern: { employeeId: "uuid" }
 */
export const EmployeeIdParamSchema = z.object({
    employeeId: uuidSchema,
});
/**
 * Location ID parameter validation
 * Used for: route parameters like /locations/:locationId/shifts
 * Pattern: { locationId: "uuid" }
 */
export const LocationIdParamSchema = z.object({
    locationId: uuidSchema,
});
/**
 * Job Role ID parameter validation
 * Used for: route parameters like /job-roles/:jobRoleId
 * Pattern: { jobRoleId: "uuid" }
 */
export const JobRoleIdParamSchema = z.object({
    jobRoleId: uuidSchema,
});
/**
 * Date range query parameters
 * Used for: filtering shifts by time range
 * Automatically coerces ISO date strings to Date objects
 */
export const DateRangeQuerySchema = z.object({
    startDate: dateSchema.optional(),
    endDate: dateSchema.optional(),
}).refine((data) => {
    // If both dates provided, endDate must be after startDate
    if (data.startDate && data.endDate) {
        return data.endDate > data.startDate;
    }
    return true;
}, {
    message: "End date must be after start date",
    path: ["endDate"],
});
